"use client";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@cmp-src/primitives/collapsible";
import { ExternalLink } from "@cmp-src/primitives/external-link";
import { parseISO } from "date-fns";
import Link from "next/link";
import React, { useMemo, useState } from "react";
import {
  AuthenticatedUser,
  IOfficesSchedulesIndexed,
  ISchedule,
  ISlot
} from "@gettactic/api";
import { groupBy } from "@/lib/utils";
import { Alert } from "@/ui/index";
import { ReservationCardList } from "@/features/reservation/components/ReservationCard/ReservationCardList";
import { ReservationCardListDateTitle } from "@/features/reservation/components/ReservationCard/ReservationCardListDateTitle";
import {
  FilterByUserAndResource,
  FormatOptionLabelProps
} from "@/features/dashboard/components/FilterByUserAndResource";
import { NotReservations } from "@/features/dashboard/components/reservations/NoReservations";
import { ReservationProps } from "../Reservations";

function AdminDateCollapsible({
  date,
  schedulesAll,
  schedulesMatching,
  embedded,
  onShowingAnotherModal
}: {
  date: string;
  schedulesAll: ISchedule[];
  schedulesMatching: ISchedule[];
  embedded: boolean;
  onShowingAnotherModal: ReservationProps["onShowingAnotherModal"];
}) {
  const [open, setOpen] = useState(true);

  return (
    <div className="ml-1 mr-1">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger>
          <ReservationCardListDateTitle open={open} date={parseISO(date)} />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ReservationCardList
            listType="admin-all"
            schedules={schedulesAll.filter((x) =>
              schedulesMatching.find((s) => s.id === x.id)
            )}
            embedded={embedded}
            onShowingAnotherModal={onShowingAnotherModal}
          />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

type Props = {
  user: AuthenticatedUser;
  officeId: string | null;
  scheduleOccupiedData: IOfficesSchedulesIndexed | null;
  embedded?: boolean;
  onShowingAnotherModal: ReservationProps["onShowingAnotherModal"];
};

type GroupByType = {
  [dateIdx: string]: ISchedule[];
};

function getSlotsInSchedules(schedules: ISchedule[]): ISlot[] {
  return removeDuplicates(schedules.flatMap((schedule) => schedule.slots));
}

function removeDuplicates<T extends { id: string }[]>(s: T): T {
  const n: any[] = [];
  s.forEach((e) => {
    if (!n.find((x) => x.id === e.id)) {
      n.push(e);
    }
  });
  return n as T;
}

function filterScheduleDataByUserAndResource(
  scheduleData: IOfficesSchedulesIndexed | null,
  query: FormatOptionLabelProps[]
): GroupByType | undefined {
  if (!scheduleData?.dates) return;

  if (query.length === 0) {
    return scheduleData.dates;
  }

  const users = query.filter((d) => d.type === "user").map((d) => d.value);
  const resources = query
    .filter((d) => d.type === "resource")
    .map((d) => d.value);
  const scheduleDataGroupByDateFiltered: GroupByType = {};

  Object.keys(scheduleData.dates).map((date) => {
    const elements = scheduleData.dates[date].filter((element) => {
      const filterByUser =
        users.length === 0 || users.includes(element.user.id);
      const filterByResource =
        resources.length === 0 || resources.includes(element.resource.id);
      return filterByUser && filterByResource;
    });

    if (elements.length > 0) {
      scheduleDataGroupByDateFiltered[date] = elements;
    }
  });

  return scheduleDataGroupByDateFiltered;
}

export function TabAdmin({
  user,
  officeId,
  scheduleOccupiedData,
  embedded = false,
  onShowingAnotherModal
}: Props) {
  const admin =
    officeId &&
    scheduleOccupiedData &&
    Object.keys(scheduleOccupiedData?.dates).length > 0;
  const [scheduleOccupiedQuery, setScheduleOccupiedQuery] = useState<
    FormatOptionLabelProps[]
  >([]);

  const scheduleOccupiedDataGroupByDate = useMemo(
    () =>
      filterScheduleDataByUserAndResource(
        scheduleOccupiedData,
        scheduleOccupiedQuery
      ),
    [scheduleOccupiedData, scheduleOccupiedQuery]
  );

  const slugPrefix = user.organization?.slug || "";
  const scheme = process.env.NEXT_PUBLIC_TACTIC_SCHEME;
  const port = process.env.NEXT_PUBLIC_TACTIC_PORT
    ? `:${process.env.NEXT_PUBLIC_TACTIC_PORT}`
    : "";
  const domain = `${slugPrefix ? slugPrefix + "." : ""}${process.env.NEXT_PUBLIC_TACTIC_DOMAIN}`;
  const adminUrl = `${scheme}://${domain}${port}/admin`;

  const dates = useMemo(() => {
    const dates = Object.keys(scheduleOccupiedDataGroupByDate ?? {});
    dates.sort();
    return dates;
  }, [scheduleOccupiedDataGroupByDate]);

  return (
    <>
      <Alert
        variant="tacticAlert"
        canDismiss={false}
        alertTitle=""
        alertMessage=""
        className="mt-2 px-4 py-2"
      >
        <p className="-mt-2 text-base">
          To manage reservations, please use the{" "}
          {embedded ? (
            <ExternalLink
              href={adminUrl}
              className="cursor-pointer text-primary underline"
            >
              new admin dashboard.
            </ExternalLink>
          ) : (
            <Link href={`/admin`}>
              <a className="cursor-pointer text-primary underline">
                new admin dashboard.
              </a>
            </Link>
          )}
        </p>
      </Alert>
      {admin ? (
        <>
          <div className="mt-5 mr-1 ml-1">
            <FilterByUserAndResource
              schedules={scheduleOccupiedData}
              onChange={setScheduleOccupiedQuery}
            />
          </div>
          {scheduleOccupiedDataGroupByDate ? (
            dates.map((date) => {
              const groupFn = (s: ISchedule) => `${s.resource.id}-${s.id}`;
              const schedulesGroupedById = groupBy(
                scheduleOccupiedDataGroupByDate[date],
                groupFn
              );
              const schedulesGroupedByIdAll = groupBy(
                scheduleOccupiedData.dates[date],
                groupFn
              );
              const schedulesMatching: ISchedule[] = [];
              const schedulesAll: ISchedule[] = [];

              Object.values(schedulesGroupedById).forEach((s) => {
                const schedule = s[0];
                const slots = getSlotsInSchedules(s);
                if (schedule) {
                  schedulesMatching.push({ ...schedule, slots });
                }
              });
              Object.values(schedulesGroupedByIdAll).forEach((s) => {
                const schedule = s[0];
                const slots = getSlotsInSchedules(s);
                if (schedule) {
                  schedulesAll.push({ ...schedule, slots });
                }
              });

              return schedulesMatching.length > 0 ? (
                <AdminDateCollapsible
                  key={date}
                  date={date}
                  schedulesAll={schedulesAll}
                  schedulesMatching={schedulesMatching}
                  embedded={embedded}
                  onShowingAnotherModal={onShowingAnotherModal}
                />
              ) : null;
            })
          ) : (
            <NotReservations />
          )}
        </>
      ) : (
        <NotReservations />
      )}
    </>
  );
}
